/**
 * Payment Links (charity collections): pure helpers for the dining app.
 * Not to be confused with lib/payment-links.ts, which builds invoice payment URLs.
 *
 * Fee maths and validation mirror backend/functions/src/paymentLinks/validation.ts.
 * The server re-checks everything.
 */

import type {
  PaymentLink,
  PaymentLinkActivity,
  PaymentLinkAmountType,
  PaymentLinkConfigInput,
  PaymentLinkFeeMode,
  PaymentLinkMethod,
  PaymentLinkPayment,
  PaymentLinkPaymentStatus,
  PaymentLinkStatus,
} from '@masonic-tools/shared-types';

export const MIN_AMOUNT_PENCE = 100;
export const MAX_AMOUNT_PENCE = 1_000_000;
export const REFERENCE_MAX_LENGTH = 18;
export const TITLE_MAX_LENGTH = 80;
export const DESCRIPTION_MAX_LENGTH = 500;
export const THANK_YOU_MAX_LENGTH = 300;
export const MESSAGE_MAX_LENGTH = 200;
export const MAX_SUGGESTED_AMOUNTS = 4;
export const MAX_EXTRA_RECIPIENTS = 5;
export const EXPIRING_SOON_MS = 3 * 24 * 60 * 60 * 1000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UK_POSTCODE_RE = /^(GIR ?0AA|[A-PR-UWYZ][A-HK-Y]?\d[A-Z\d]? ?\d[ABD-HJLNP-UW-Z]{2})$/i;

export const METHOD_LABELS: Record<PaymentLinkMethod, string> = {
  stripe: 'Card (Stripe)',
  sumup: 'Card (SumUp)',
  slickpay: 'Pay by bank (SlickPay)',
};

export const METHOD_DESCRIPTIONS: Record<PaymentLinkMethod, string> = {
  stripe: 'Card, Apple Pay and Google Pay',
  sumup: 'Card payments through your SumUp account',
  slickpay: 'Pay by bank — no card needed',
};

export const METHOD_PROVIDER_NAMES: Record<PaymentLinkMethod, string> = {
  stripe: 'Stripe',
  sumup: 'SumUp',
  slickpay: 'SlickPay',
};

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

export function formatPence(pence: number | null | undefined): string {
  const p = typeof pence === 'number' && Number.isFinite(pence) ? pence : 0;
  return `£${(p / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Short form: £5, £12.50. */
export function formatPenceShort(pence: number | null | undefined): string {
  const p = typeof pence === 'number' && Number.isFinite(pence) ? pence : 0;
  return p % 100 === 0 ? `£${(p / 100).toLocaleString('en-GB')}` : formatPence(p);
}

/** Parse what someone typed in a £ field ("12", "12.5", "£1,000.00") into pence, or null. */
export function parsePoundsInput(raw: string): number | null {
  const cleaned = raw.replace(/[£,\s]/g, '');
  if (!cleaned) return null;
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function penceToInput(pence: number | null | undefined): string {
  if (typeof pence !== 'number' || !Number.isFinite(pence)) return '';
  return pence % 100 === 0 ? String(pence / 100) : (pence / 100).toFixed(2);
}

export interface FeeBreakdown {
  amountPence: number;
  processingFeePence: number;
  totalChargedPence: number;
  netToUnitPence: number;
}

export function computeFeeBreakdown(feeMode: PaymentLinkFeeMode, amountPence: number, feePence: number): FeeBreakdown {
  const fee = Math.max(0, Math.round(feePence));
  if (feeMode === 'on_top') {
    return { amountPence, processingFeePence: fee, totalChargedPence: amountPence + fee, netToUnitPence: amountPence };
  }
  return { amountPence, processingFeePence: fee, totalChargedPence: amountPence, netToUnitPence: amountPence - fee };
}

export function giftAidValuePence(eligiblePence: number): number {
  return Math.floor(eligiblePence * 0.25);
}

export function amountRuleLabel(link: Pick<PaymentLink, 'amountType' | 'fixedAmountPence' | 'minAmountPence' | 'maxAmountPence'>): string {
  if (link.amountType === 'fixed') return formatPenceShort(link.fixedAmountPence);
  if (link.amountType === 'range') return `${formatPenceShort(link.minAmountPence)}–${formatPenceShort(link.maxAmountPence)}`;
  return 'Any amount';
}

export function feeModeShortLabel(mode: PaymentLinkFeeMode): string {
  return mode === 'on_top' ? '+fee' : 'incl.';
}

/** Worked example for the fee options in the form. */
export function feeExampleText(mode: PaymentLinkFeeMode, amountPence: number, feePence: number): string {
  const b = computeFeeBreakdown(mode, amountPence, feePence);
  if (mode === 'on_top') {
    return `Payer pays ${formatPence(b.amountPence)} + ${formatPence(b.processingFeePence)} = ${formatPence(b.totalChargedPence)}, you receive ${formatPence(b.netToUnitPence)}`;
  }
  return `Payer pays ${formatPence(b.totalChargedPence)}, you receive ${formatPence(b.netToUnitPence)}`;
}

/** Lowest amount a payer could choose, used for the fee example and fee checks. */
export function minimumAmountForLink(link: Pick<PaymentLinkConfigInput, 'amountType' | 'fixedAmountPence' | 'minAmountPence'>): number {
  if (link.amountType === 'fixed') return link.fixedAmountPence ?? MIN_AMOUNT_PENCE;
  if (link.amountType === 'range') return link.minAmountPence ?? MIN_AMOUNT_PENCE;
  return MIN_AMOUNT_PENCE;
}

// ---------------------------------------------------------------------------
// Reference
// ---------------------------------------------------------------------------

/** Upper-case, spaces to hyphens, only A–Z 0–9 & - . / (what bank statements accept). */
export function normalizeReference(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9&./-]/g, '')
    .replace(/-{2,}/g, '-');
}

// ---------------------------------------------------------------------------
// Form validation (client side; the server repeats it)
// ---------------------------------------------------------------------------

export type FormErrors = Partial<Record<string, string>>;

export function validatePaymentLinkForm(
  input: PaymentLinkConfigInput,
  options: { feePence: number; nowMs: number; availableMethods: PaymentLinkMethod[]; existingExpiresAtMs?: number | null },
): FormErrors {
  const errors: FormErrors = {};
  const title = input.title.trim();
  if (!title) errors.title = 'Give the link a title';
  else if (title.length > TITLE_MAX_LENGTH) errors.title = `Keep the title to ${TITLE_MAX_LENGTH} characters`;
  if (input.description.length > DESCRIPTION_MAX_LENGTH) errors.description = `Keep the description to ${DESCRIPTION_MAX_LENGTH} characters`;
  if (input.thankYouMessage.length > THANK_YOU_MAX_LENGTH) errors.thankYouMessage = `Keep the message to ${THANK_YOU_MAX_LENGTH} characters`;

  const ref = normalizeReference(input.reference);
  if (!ref) errors.reference = 'Add a reference';
  else if (ref.length > REFERENCE_MAX_LENGTH) errors.reference = `References can be up to ${REFERENCE_MAX_LENGTH} characters`;

  const checkAmount = (pence: number | null, field: string, label: string) => {
    if (pence === null) errors[field] = `Enter ${label}`;
    else if (pence < MIN_AMOUNT_PENCE) errors[field] = `Minimum is ${formatPence(MIN_AMOUNT_PENCE)}`;
    else if (pence > MAX_AMOUNT_PENCE) errors[field] = `Maximum is ${formatPence(MAX_AMOUNT_PENCE)}`;
  };
  if (input.amountType === 'fixed') checkAmount(input.fixedAmountPence, 'fixedAmountPence', 'an amount');
  if (input.amountType === 'range') {
    checkAmount(input.minAmountPence, 'minAmountPence', 'a minimum');
    checkAmount(input.maxAmountPence, 'maxAmountPence', 'a maximum');
    if (!errors.minAmountPence && !errors.maxAmountPence && (input.minAmountPence ?? 0) >= (input.maxAmountPence ?? 0)) {
      errors.maxAmountPence = 'Maximum must be more than the minimum';
    }
  }
  if (input.amountType !== 'fixed') {
    const lo = input.amountType === 'range' ? input.minAmountPence ?? MIN_AMOUNT_PENCE : MIN_AMOUNT_PENCE;
    const hi = input.amountType === 'range' ? input.maxAmountPence ?? MAX_AMOUNT_PENCE : MAX_AMOUNT_PENCE;
    if (input.suggestedAmountsPence.length > MAX_SUGGESTED_AMOUNTS) errors.suggestedAmountsPence = `Add up to ${MAX_SUGGESTED_AMOUNTS} amounts`;
    else if (input.suggestedAmountsPence.some((v) => v < lo || v > hi)) errors.suggestedAmountsPence = 'Suggested amounts must be within the range';
  }

  if (!options.availableMethods.includes(input.paymentMethod)) errors.paymentMethod = 'Choose a payment method that is set up';
  // "Any amount" links are always allowed; each payment must just be more than the fee.
  if (input.feeMode === 'included' && input.amountType !== 'open' && !errors.fixedAmountPence && !errors.minAmountPence) {
    const min = minimumAmountForLink(input);
    if (min <= options.feePence) {
      errors.feeMode = `With the fee included, the ${input.amountType === 'range' ? 'minimum' : 'amount'} must be more than the ${formatPence(options.feePence)} fee`;
    }
  }

  if (input.expiresAt) {
    const ms = Date.parse(input.expiresAt);
    if (!Number.isFinite(ms)) errors.expiresAt = 'Choose a valid date and time';
    else if (ms <= options.nowMs && ms !== options.existingExpiresAtMs) errors.expiresAt = 'Expiry must be in the future';
  }

  if (input.notifications.extraRecipients.length > MAX_EXTRA_RECIPIENTS) {
    errors.extraRecipients = `Add up to ${MAX_EXTRA_RECIPIENTS} extra recipients`;
  } else {
    const bad = input.notifications.extraRecipients.find((e) => !EMAIL_RE.test(e));
    if (bad) errors.extraRecipients = `${bad} is not a valid email address`;
  }
  return errors;
}

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

export function isValidUkPostcode(postcode: string): boolean {
  return UK_POSTCODE_RE.test(postcode.trim());
}

export interface PayerFormInput {
  amountPence: number | null;
  giveAnonymously: boolean;
  payerName: string;
  payerEmail: string;
  message: string;
  giftAid: boolean;
  giftAidTitle: string;
  giftAidFirstName: string;
  giftAidLastName: string;
  giftAidHouse: string;
  giftAidPostcode: string;
  giftAidConfirmed: boolean;
}

export interface PublicLinkRules {
  amountType: PaymentLinkAmountType;
  fixedAmountPence: number | null;
  minAmountPence: number | null;
  maxAmountPence: number | null;
  feeMode: PaymentLinkFeeMode;
  feePence: number;
  giftAidEnabled: boolean;
}

/** Smallest amount a payer can pay: the link minimum, or 1p over the fee when the fee is included. */
export function effectiveMinimumPence(link: Pick<PublicLinkRules, 'amountType' | 'fixedAmountPence' | 'minAmountPence' | 'feeMode' | 'feePence'>): number {
  const min = minimumAmountForLink(link);
  return link.feeMode === 'included' ? Math.max(min, link.feePence + 1) : min;
}

export function validateAmountForPublicLink(link: PublicLinkRules, amountPence: number | null): string | null {
  if (link.amountType === 'fixed') return null;
  if (amountPence === null) return 'Enter an amount';
  const min = link.amountType === 'range' ? link.minAmountPence ?? MIN_AMOUNT_PENCE : MIN_AMOUNT_PENCE;
  const max = link.amountType === 'range' ? link.maxAmountPence ?? MAX_AMOUNT_PENCE : MAX_AMOUNT_PENCE;
  if (amountPence < min) return `Minimum is ${formatPence(min)}`;
  if (amountPence > max) return `Maximum is ${formatPence(max)}`;
  if (link.feeMode === 'included' && amountPence <= link.feePence) return `Minimum is ${formatPence(effectiveMinimumPence(link))}`;
  return null;
}

/** Whether the payer's name and email are complete enough to offer Gift Aid. */
export function canOfferGiftAid(link: PublicLinkRules, form: PayerFormInput): boolean {
  return link.giftAidEnabled && !form.giveAnonymously && form.payerName.trim().length > 0 && isValidEmail(form.payerEmail);
}

export function validatePayerForm(link: PublicLinkRules, form: PayerFormInput): FormErrors {
  const errors: FormErrors = {};
  const amountError = validateAmountForPublicLink(link, form.amountPence);
  if (amountError) errors.amount = amountError;
  if (!form.giveAnonymously) {
    if (!form.payerName.trim()) errors.payerName = 'Enter your name';
    if (!form.payerEmail.trim()) errors.payerEmail = 'Enter your email';
    else if (!isValidEmail(form.payerEmail)) errors.payerEmail = 'Enter a valid email address';
  }
  if (form.message.length > MESSAGE_MAX_LENGTH) errors.message = `Keep your message to ${MESSAGE_MAX_LENGTH} characters`;
  if (form.giftAid && canOfferGiftAid(link, form)) {
    if (!form.giftAidFirstName.trim()) errors.giftAidFirstName = 'Enter your first name';
    if (!form.giftAidLastName.trim()) errors.giftAidLastName = 'Enter your last name';
    if (!form.giftAidHouse.trim()) errors.giftAidHouse = 'Enter your house name or number';
    if (!isValidUkPostcode(form.giftAidPostcode)) errors.giftAidPostcode = 'Enter a valid UK postcode';
    if (!form.giftAidConfirmed) errors.giftAidConfirmed = 'Tick to confirm you are a UK taxpayer';
  }
  return errors;
}

/** Split "Jo Bloggs" into first and last name for the Gift Aid fields. */
export function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

export function fillGiftAidDeclaration(template: string, amountPence: number, recipient: string): string {
  return template.replace('{amount}', formatPence(amountPence)).replace('{recipient}', recipient);
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type LinkDisplayState = 'active' | 'expiring' | 'expired' | 'disabled' | 'archived' | 'method_unavailable';

export function linkDisplayState(
  link: Pick<PaymentLink, 'status' | 'expiresAt' | 'paymentMethod'>,
  nowMs: number,
  availableMethods?: PaymentLinkMethod[],
): LinkDisplayState {
  if (link.status === 'archived') return 'archived';
  if (link.status === 'disabled') return 'disabled';
  const expiresMs = link.expiresAt ? link.expiresAt.getTime() : null;
  if (expiresMs !== null && expiresMs <= nowMs) return 'expired';
  if (availableMethods && !availableMethods.includes(link.paymentMethod)) return 'method_unavailable';
  if (expiresMs !== null && expiresMs - nowMs <= EXPIRING_SOON_MS) return 'expiring';
  return 'active';
}

export type LinkListTab = 'active' | 'expired' | 'disabled' | 'archived' | 'all';

export function linkMatchesTab(state: LinkDisplayState, tab: LinkListTab): boolean {
  if (tab === 'all') return state !== 'archived';
  if (tab === 'active') return state === 'active' || state === 'expiring' || state === 'method_unavailable';
  return state === tab;
}

export function relativeDays(targetMs: number, nowMs: number): string {
  const diff = targetMs - nowMs;
  const days = Math.round(Math.abs(diff) / (24 * 60 * 60 * 1000));
  const hours = Math.round(Math.abs(diff) / (60 * 60 * 1000));
  const unit = days >= 1 ? `${days} day${days === 1 ? '' : 's'}` : `${Math.max(1, hours)} hour${hours === 1 ? '' : 's'}`;
  return diff >= 0 ? `in ${unit}` : `${unit} ago`;
}

export function formatDateTime(date: Date | null | undefined): string {
  if (!date) return '';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Firestore mapping
// ---------------------------------------------------------------------------

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v && 'toDate' in v && typeof (v as { toDate: unknown }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  if (typeof v === 'number') return new Date(v);
  return null;
}

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const nOrNull = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const s = (v: unknown) => (typeof v === 'string' ? v : '');

export function mapPaymentLinkDoc(id: string, unitId: string, d: Record<string, any>): PaymentLink {
  const notify = d.notifications || {};
  return {
    id,
    unitId,
    title: s(d.title),
    description: s(d.description),
    reference: s(d.reference),
    thankYouMessage: s(d.thankYouMessage),
    amountType: d.amountType === 'fixed' || d.amountType === 'range' ? d.amountType : 'open',
    fixedAmountPence: nOrNull(d.fixedAmountPence),
    minAmountPence: nOrNull(d.minAmountPence),
    maxAmountPence: nOrNull(d.maxAmountPence),
    suggestedAmountsPence: Array.isArray(d.suggestedAmountsPence) ? d.suggestedAmountsPence.filter((x: unknown) => typeof x === 'number') : [],
    feeMode: d.feeMode === 'included' ? 'included' : 'on_top',
    paymentMethod: (['stripe', 'sumup', 'slickpay'].includes(d.paymentMethod) ? d.paymentMethod : 'stripe') as PaymentLinkMethod,
    giftAidEnabled: d.giftAidEnabled === true,
    expiresAt: toDate(d.expiresAt),
    status: (d.status === 'disabled' || d.status === 'archived' ? d.status : 'active') as PaymentLinkStatus,
    notifications: {
      mode: notify.mode === 'daily' || notify.mode === 'off' ? notify.mode : 'each',
      extraRecipients: Array.isArray(notify.extraRecipients) ? notify.extraRecipients : [],
      expiryReminder: notify.expiryReminder === true,
    },
    targetUrl: s(d.targetUrl),
    shortUrl: s(d.shortUrl) || s(d.targetUrl),
    shortenerUsed: d.shortenerUsed === true,
    totalCollectedPence: n(d.totalCollectedPence),
    totalNetPence: n(d.totalNetPence),
    totalFeesPence: n(d.totalFeesPence),
    paymentCount: n(d.paymentCount),
    giftAidCount: n(d.giftAidCount),
    giftAidEligiblePence: n(d.giftAidEligiblePence),
    lastPaymentAt: toDate(d.lastPaymentAt),
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    createdBy: s(d.createdBy),
    createdByName: s(d.createdByName),
  };
}

export function mapPaymentDoc(id: string, unitId: string, linkId: string, d: Record<string, any>): PaymentLinkPayment {
  const g = d.giftAid;
  return {
    id,
    unitId,
    linkId,
    status: (['pending', 'paid', 'failed', 'cancelled'].includes(d.status) ? d.status : 'pending') as PaymentLinkPaymentStatus,
    method: (d.method || 'stripe') as PaymentLinkMethod,
    feeMode: d.feeMode === 'included' ? 'included' : 'on_top',
    reference: s(d.reference),
    amountPence: n(d.amountPence),
    processingFeePence: n(d.processingFeePence),
    totalChargedPence: n(d.totalChargedPence),
    netToUnitPence: n(d.netToUnitPence),
    anonymous: d.anonymous !== false,
    payerName: typeof d.payerName === 'string' ? d.payerName : null,
    payerEmail: typeof d.payerEmail === 'string' ? d.payerEmail : null,
    message: typeof d.message === 'string' && d.message ? d.message : null,
    giftAid: g
      ? {
          title: s(g.title),
          firstName: s(g.firstName),
          lastName: s(g.lastName),
          houseNameOrNumber: s(g.houseNameOrNumber),
          postcode: s(g.postcode),
          declarationVersion: s(g.declarationVersion),
          declaredAt: toDate(g.declaredAt),
          eligiblePence: n(g.eligiblePence),
        }
      : null,
    stripeSessionId: d.stripeSessionId,
    stripePaymentIntentId: d.stripePaymentIntentId,
    sumupCheckoutId: d.sumupCheckoutId,
    sumupTransactionCode: d.sumupTransactionCode,
    slickpayPoId: d.slickpayPoId,
    creditsDebited: typeof d.creditsDebited === 'number' ? d.creditsDebited : undefined,
    receiptSentAt: toDate(d.receiptSentAt),
    receiptSendCount: n(d.receiptSendCount),
    leadsNotifiedAt: toDate(d.leadsNotifiedAt),
    closedReason: typeof d.closedReason === 'string' ? d.closedReason : undefined,
    statusHistory: Array.isArray(d.statusHistory)
      ? d.statusHistory.map((h: any) => ({ status: h.status, at: toDate(h.at), source: h.source, note: h.note }))
      : [],
    createdAt: toDate(d.createdAt),
    paidAt: toDate(d.paidAt),
  };
}

export function mapActivityDoc(id: string, d: Record<string, any>): PaymentLinkActivity {
  return {
    id,
    action: s(d.action),
    message: s(d.message),
    actorName: typeof d.actorName === 'string' ? d.actorName : null,
    createdAt: toDate(d.createdAt),
  };
}

/** Turn a stored link back into form input for editing. */
export function linkToFormInput(link: PaymentLink): PaymentLinkConfigInput {
  return {
    title: link.title,
    description: link.description,
    reference: link.reference,
    thankYouMessage: link.thankYouMessage,
    amountType: link.amountType,
    fixedAmountPence: link.fixedAmountPence,
    minAmountPence: link.minAmountPence,
    maxAmountPence: link.maxAmountPence,
    suggestedAmountsPence: link.suggestedAmountsPence,
    feeMode: link.feeMode,
    paymentMethod: link.paymentMethod,
    giftAidEnabled: link.giftAidEnabled,
    expiresAt: link.expiresAt ? link.expiresAt.toISOString() : null,
    notifications: { ...link.notifications },
  };
}

export function emptyFormInput(defaultMethod: PaymentLinkMethod): PaymentLinkConfigInput {
  return {
    title: '',
    description: '',
    reference: '',
    thankYouMessage: '',
    amountType: 'open',
    fixedAmountPence: null,
    minAmountPence: null,
    maxAmountPence: null,
    suggestedAmountsPence: [],
    feeMode: 'on_top',
    paymentMethod: defaultMethod,
    giftAidEnabled: false,
    expiresAt: null,
    notifications: { mode: 'each', extraRecipients: [], expiryReminder: true },
  };
}

// ---------------------------------------------------------------------------
// Payments table: filters and totals
// ---------------------------------------------------------------------------

export type PaymentStatusFilter = PaymentLinkPaymentStatus | 'all';
export type GiftAidFilter = 'all' | 'yes' | 'no';
export type DateRangeFilter = 'all' | 'today' | '7d' | 'month' | 'custom';

export interface PaymentFilters {
  search: string;
  status: PaymentStatusFilter;
  giftAid: GiftAidFilter;
  dateRange: DateRangeFilter;
  customFrom?: string;
  customTo?: string;
}

export const DEFAULT_PAYMENT_FILTERS: PaymentFilters = { search: '', status: 'paid', giftAid: 'all', dateRange: 'all' };

function paymentDate(p: PaymentLinkPayment): Date | null {
  return p.paidAt || p.createdAt;
}

export function dateRangeBounds(filters: PaymentFilters, now: Date): { from: number | null; to: number | null } {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  switch (filters.dateRange) {
    case 'today':
      return { from: startOfToday, to: null };
    case '7d':
      return { from: startOfToday - 6 * 24 * 60 * 60 * 1000, to: null };
    case 'month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), to: null };
    case 'custom': {
      const from = filters.customFrom ? Date.parse(`${filters.customFrom}T00:00:00`) : NaN;
      const to = filters.customTo ? Date.parse(`${filters.customTo}T23:59:59.999`) : NaN;
      return { from: Number.isFinite(from) ? from : null, to: Number.isFinite(to) ? to : null };
    }
    default:
      return { from: null, to: null };
  }
}

export function filterPayments(payments: PaymentLinkPayment[], filters: PaymentFilters, now: Date = new Date()): PaymentLinkPayment[] {
  const q = filters.search.trim().toLowerCase();
  const { from, to } = dateRangeBounds(filters, now);
  return payments.filter((p) => {
    if (filters.status !== 'all' && p.status !== filters.status) return false;
    if (filters.giftAid === 'yes' && !p.giftAid) return false;
    if (filters.giftAid === 'no' && p.giftAid) return false;
    const d = paymentDate(p)?.getTime() ?? null;
    if (from !== null && (d === null || d < from)) return false;
    if (to !== null && (d === null || d > to)) return false;
    if (q) {
      const hay = [p.payerName, p.payerEmail, p.stripePaymentIntentId, p.sumupTransactionCode, p.sumupCheckoutId, p.slickpayPoId, p.id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export interface PaymentTotals {
  count: number;
  grossPence: number;
  feesPence: number;
  totalChargedPence: number;
  netPence: number;
  giftAidCount: number;
  giftAidEligiblePence: number;
}

/** Totals over paid payments only (pending/failed rows never count towards money in). */
export function totalPayments(payments: PaymentLinkPayment[]): PaymentTotals {
  const totals: PaymentTotals = { count: 0, grossPence: 0, feesPence: 0, totalChargedPence: 0, netPence: 0, giftAidCount: 0, giftAidEligiblePence: 0 };
  for (const p of payments) {
    if (p.status !== 'paid') continue;
    totals.count++;
    totals.grossPence += p.amountPence;
    totals.feesPence += p.processingFeePence;
    totals.totalChargedPence += p.totalChargedPence;
    totals.netPence += p.netToUnitPence;
    if (p.giftAid) {
      totals.giftAidCount++;
      totals.giftAidEligiblePence += p.giftAid.eligiblePence || p.amountPence;
    }
  }
  return totals;
}

export type SortKey = 'date' | 'payer' | 'amount' | 'fee' | 'total' | 'net' | 'method' | 'giftAid' | 'status';

export function sortPayments(payments: PaymentLinkPayment[], key: SortKey, dir: 'asc' | 'desc'): PaymentLinkPayment[] {
  const sign = dir === 'asc' ? 1 : -1;
  const value = (p: PaymentLinkPayment): string | number => {
    switch (key) {
      case 'payer':
        return (p.anonymous ? '' : p.payerName || '').toLowerCase();
      case 'amount':
        return p.amountPence;
      case 'fee':
        return p.processingFeePence;
      case 'total':
        return p.totalChargedPence;
      case 'net':
        return p.netToUnitPence;
      case 'method':
        return p.method;
      case 'giftAid':
        return p.giftAid ? 1 : 0;
      case 'status':
        return p.status;
      default:
        return paymentDate(p)?.getTime() ?? 0;
    }
  };
  return [...payments].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    return 0;
  });
}

export function providerTransactionId(p: PaymentLinkPayment): string {
  return p.stripePaymentIntentId || p.sumupTransactionCode || p.sumupCheckoutId || p.slickpayPoId || '';
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export function csvEscape(value: unknown): string {
  const str = value === null || value === undefined ? '' : String(value);
  // Neutralise spreadsheet formulas from payer-entered text.
  const safe = /^[=+\-@\t\r]/.test(str) ? `'${str}` : str;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv(rows: Array<Array<unknown>>): string {
  return rows.map((r) => r.map(csvEscape).join(',')).join('\r\n') + '\r\n';
}

const pounds = (pence: number) => (pence / 100).toFixed(2);

function datePart(d: Date | null): string {
  return d ? d.toLocaleDateString('en-GB') : '';
}

function timePart(d: Date | null): string {
  return d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
}

export const PAYMENTS_CSV_HEADERS = [
  'Date',
  'Time',
  'Payment link',
  'Reference',
  'Payer name',
  'Email',
  'Anonymous',
  'Amount (£)',
  'Fee (£)',
  'Total charged (£)',
  'Net to unit (£)',
  'Method',
  'Provider transaction ID',
  'Gift Aid',
  'Status',
  'Message',
];

export function paymentsCsvRows(payments: PaymentLinkPayment[], linkTitles: Record<string, string>): Array<Array<unknown>> {
  return payments.map((p) => {
    const d = paymentDate(p);
    return [
      datePart(d),
      timePart(d),
      linkTitles[p.linkId] || '',
      p.reference,
      p.anonymous ? '' : p.payerName || '',
      p.anonymous ? '' : p.payerEmail || '',
      p.anonymous ? 'Yes' : 'No',
      pounds(p.amountPence),
      pounds(p.processingFeePence),
      pounds(p.totalChargedPence),
      pounds(p.netToUnitPence),
      METHOD_PROVIDER_NAMES[p.method] || p.method,
      providerTransactionId(p),
      p.giftAid ? 'Yes' : 'No',
      p.status,
      p.message || '',
    ];
  });
}

export function buildPaymentsCsv(payments: PaymentLinkPayment[], linkTitles: Record<string, string>): string {
  return toCsv([PAYMENTS_CSV_HEADERS, ...paymentsCsvRows(payments, linkTitles)]);
}

/** Column layout of the HMRC Charities Online Gift Aid schedule. */
export const GIFT_AID_CSV_HEADERS = [
  'Title',
  'First name',
  'Last name',
  'House name or number',
  'Postcode',
  'Aggregated donations',
  'Sponsored event',
  'Donation date',
  'Amount',
];

function hmrcDate(d: Date | null): string {
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

/** Only paid payments with a Gift Aid declaration. */
export function giftAidPayments(payments: PaymentLinkPayment[]): PaymentLinkPayment[] {
  return payments.filter((p) => p.status === 'paid' && p.giftAid);
}

export function giftAidCsvRows(payments: PaymentLinkPayment[]): Array<Array<unknown>> {
  return giftAidPayments(payments).map((p) => [
    p.giftAid!.title || '',
    p.giftAid!.firstName,
    p.giftAid!.lastName,
    p.giftAid!.houseNameOrNumber,
    p.giftAid!.postcode,
    '',
    '',
    hmrcDate(p.paidAt || p.createdAt),
    pounds(p.giftAid!.eligiblePence || p.amountPence),
  ]);
}

export function buildGiftAidCsv(payments: PaymentLinkPayment[]): string {
  return toCsv([GIFT_AID_CSV_HEADERS, ...giftAidCsvRows(payments)]);
}

export function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'payment-link'
  );
}

export function exportFileName(reference: string, kind: 'payments' | 'gift-aid' | 'summary', date: Date, ext: string): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${slugify(reference)}-${y}-${m}-${d}-${kind}.${ext}`;
}

/** Group paid payments by calendar day (for the PDF summary). */
export function paymentsByDay(payments: PaymentLinkPayment[]): Array<{ day: string; count: number; grossPence: number; netPence: number }> {
  const map = new Map<string, { day: string; count: number; grossPence: number; netPence: number; sort: number }>();
  for (const p of payments) {
    if (p.status !== 'paid') continue;
    const d = p.paidAt || p.createdAt;
    if (!d) continue;
    const key = d.toLocaleDateString('en-GB');
    const row = map.get(key) || { day: key, count: 0, grossPence: 0, netPence: 0, sort: new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() };
    row.count++;
    row.grossPence += p.amountPence;
    row.netPence += p.netToUnitPence;
    map.set(key, row);
  }
  return [...map.values()].sort((a, b) => a.sort - b.sort).map(({ sort: _sort, ...rest }) => rest);
}

export function downloadTextFile(content: string, fileName: string, mime = 'text/csv;charset=utf-8'): void {
  // BOM so Excel opens UTF-8 (e.g. £, accented names) correctly.
  const blob = new Blob([mime.startsWith('text/csv') ? '﻿' + content : content], { type: mime });
  downloadBlob(blob, fileName);
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadDataUrl(dataUrl: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Copy of the HMRC one-off declaration wording the server uses (for the form preview). */
export const GIFT_AID_DECLARATION_TEMPLATE =
  'I want to Gift Aid my donation of {amount} to {recipient}. I am a UK taxpayer and understand that ' +
  'if I pay less Income Tax and/or Capital Gains Tax than the amount of Gift Aid claimed on all my ' +
  'donations in that tax year it is my responsibility to pay any difference.';

/** `datetime-local` input value (local time) for a date. */
export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Parse a `datetime-local` value (local time) to an ISO string, or null. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

/** Quick expiry choices: end of today, or 23:59 in N days. */
export function expiryPreset(kind: 'today' | 7 | 30, now: Date = new Date()): Date {
  const d = new Date(now);
  if (kind !== 'today') d.setDate(d.getDate() + kind);
  d.setHours(23, 59, 0, 0);
  return d;
}
